import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import './control.css';
import './Input.css';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Icon placed inside the field on the leading edge. */
  leadingIcon?: ReactNode;
  /** Element placed inside the field on the trailing edge (icon or small button). */
  trailingSlot?: ReactNode;
  invalid?: boolean;
  size?: 'sm' | 'md';
}

/**
 * Single-line text control wearing the shared `.control` vocabulary. Wrap with
 * {@link Field} for labels and errors; pass the field's `controlProps` through.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { leadingIcon, trailingSlot, invalid, size = 'md', className, 'aria-invalid': ariaInvalid, ...rest },
  ref,
) {
  const isInvalid = invalid || ariaInvalid === true || ariaInvalid === 'true';
  const hasAffix = Boolean(leadingIcon || trailingSlot);

  const input = (
    <input
      ref={ref}
      className={cn(
        'control',
        `control--${size}`,
        'input',
        leadingIcon && 'input--has-leading',
        trailingSlot && 'input--has-trailing',
        !hasAffix && className,
      )}
      aria-invalid={isInvalid || undefined}
      {...rest}
    />
  );

  if (!hasAffix) return input;

  return (
    <div className={cn('input-wrap', className)} data-invalid={isInvalid || undefined}>
      {leadingIcon && (
        <span className="input-wrap__icon input-wrap__icon--leading" aria-hidden="true">
          {leadingIcon}
        </span>
      )}
      {input}
      {trailingSlot && <span className="input-wrap__icon input-wrap__icon--trailing">{trailingSlot}</span>}
    </div>
  );
});
